import { Table, Typography, Pagination, Button, Dropdown, Menu } from 'antd';
import { PlusOutlined, MinusOutlined, MoreOutlined } from '@ant-design/icons';
import { useState } from 'react'; 

const CustomCollapseTable = ({
  dataSource,
  columns,
  rowKey = 'key',
  pagination = true,
  collapsible = false,
  showMoreIcon,
  menuItems,
  currentPage = 1,
  pageSize = 10,
  total = 0,
  handlePageChange,
  showSizeChanger = false,
  pageSizeOptions = ['10', '25', '50', '100'],
  showTotalText = false,
  showTotal = (total, range) => `Showing ${range[0]}-${range[1]} of ${total} entries`,
  renderCollapseContent,
  onChildClick,
  cellHeight = '44px',
  headerBackgroundColor = '#F9FAFB',
  headerColor = 'var(--text-light-gray-color)',
  ...rest
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleExpand = (expanded, record) => {
    const key = record[rowKey];
    setExpandedRowKeys(prev =>
      expanded ? [key] : prev.filter(k => k !== key)
    );
  };

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            backgroundColor: headerBackgroundColor,
            padding: '12px 24px',
            height: '44px',
            textAlign: 'left',
            color: headerColor,
            fontSize: '12px',
            fontWeight: '500',
          }}
        />
      ),
    },
    body: {
      cell: (props) => (
        <td
          {...props}
          style={{
            backgroundColor: '#fff',
            padding: '12px 24px',
            height: cellHeight,
            textAlign: 'left',
            color: 'var(--text-black-color)',
            fontSize: '14px',
            fontWeight: '500',
          }}
        />
      ),
    },
  };

  const enhancedColumns = [
    ...columns,
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      width: 50,
      align: 'center',
      render: (_, record) => {
        const key = record[rowKey];
        const isParent = !!record.children;

        if (showMoreIcon && !isParent) {
          return (
            <Dropdown
              overlay={<Menu items={menuItems?.(record) || []} />}
              trigger={['click']}
            >
              <Button
                type="text"
                icon={<MoreOutlined />}
                className="action-button"
              />
            </Dropdown>
          );
        }

        if (collapsible && isParent) {
          return (
            <Button
              type="text"
              className="action-button"
              icon={
                expandedRowKeys.includes(key) ? (
                  <MinusOutlined style={{ color: 'var(--primary-color)' }} />
                ) : (
                  <PlusOutlined style={{ color: 'var(--primary-color)' }} />
                )
              }
              onClick={(event) => {
                event.stopPropagation();
                handleExpand(!expandedRowKeys.includes(key), record);
              }}
            />
          );
        }

        return null;
      },
    },
  ];

  return (
    <div>
      <Table
        dataSource={dataSource}
        columns={enhancedColumns}
        rowKey="key"
        pagination={false}
        components={components}
        expandable={{
          expandedRowKeys,
          onExpand: (expanded, record) => handleExpand(expanded, record),
          expandedRowRender: renderCollapseContent,
          expandIcon: () => null,
        }}
        onRow={(record) => {
          const isChild = !record.children;
          return isChild
            ? {
                onClick: (event) => {
                  if (event.target.closest('.action-button')) {
                    event.stopPropagation();
                    return;
                  }
                  onChildClick?.(record);
                },
                style: { cursor: 'pointer', background: '#f5f5f5' },
              }
            : {};
        }}
        {...rest}
      />

      {pagination !== false && (
        <div
          style={{
            marginTop: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '5px',
          }}
        >
          {showTotalText && (
            <Typography.Text>
              {showTotal(total, [
                (currentPage - 1) * pageSize + 1,
                Math.min(currentPage * pageSize, total),
              ])}
            </Typography.Text>
          )}
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={total}
            onChange={handlePageChange}
            showSizeChanger={showSizeChanger}
            pageSizeOptions={pageSizeOptions}
          />
        </div>
      )}
    </div>
  );
};

export default CustomCollapseTable;
